import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Tooltip } from "@mui/material";
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
// utilities
import { useSnackbar } from "../../../utils/useSnackbar";
import { setActivePage } from "../../../features/navigation/navigationSlice";
import { getOrganizations, deleteOrganizations } from "../../../utils/api/organization_apis";
import { STATUS } from "../../../utils/api/sharedfunction";
import { MissingData } from "../../../enums/enumRegistry";
import styles from "../../../styles/component.module.scss";

// components
import PageHeader from "../../Header/PageHeader";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import CreateOrganizationModal from "../../Modal/CreateOrganizationModal";
import EditOrganizationModal from "../../Modal/EditOrganizationModal";
import ActionMenu from "../../menu/ActionMenu";
import TableComponent from "../../TableComponent";
import RowActionMenu from "../../menu/RowActionMenu";
import { renderTableCell } from "../../../utils/commonfunctions";
import { getDeleteModalTitle, getDeleteConfirmMessage } from "../../../utils/uiText";

const Organizations = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [selectedRows, setSelectedRows] = useState([]);
  const activePage = useSelector((state) => state.navigation.activePage);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [organizationForAction, setOrganizationForAction] = useState(null);
  const isMultiAction = selectedRows.length > 0;

  useEffect(() => {
    dispatch(setActivePage('Organizations'));
    fetchOrganizations();
  }, [dispatch]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await getOrganizations();
      
      if (response.status === STATUS.SUCCESS) {
        const formattedOrgs = response.data.map(org => ({
          ...org,
          created_at: new Date(org.created_at).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        }));
        setOrganizations(formattedOrgs);
      } else {
        showSnackbar(response.error || 'Failed to fetch organizations', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      showSnackbar('Failed to fetch organizations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const orgIdsToDelete = isMultiAction ? selectedRows : [organizationForAction.id];
      const response = await deleteOrganizations({ ids: orgIdsToDelete });

      if (response.status === STATUS.SUCCESS) {
        setIsDeleting(false);
        showSnackbar(response.message || 'Organization(s) deleted successfully', 'success');
        fetchOrganizations();
        setSelectedRows([]);
      } else {
        showSnackbar(response.error || 'Failed to delete organization(s)', 'error');
      }
    } catch (error) {
      console.error('Error deleting organization(s):', error);
      showSnackbar('Failed to delete organization(s)', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Row action handlers
  const handleOrganizationEdit = (organization) => {
    setOrganizationForAction(organization);
    setIsEditing(true);
  };

  const handleOrganizationDelete = (organization) => {
    setOrganizationForAction(organization);
    setIsDeleting(true);
  };

  // Header action handlers
  const handleAdd = () => {
    setIsCreating(true);
  };

  const handleDelete = () => {
    setIsDeleting(true);
  };

  // Handler function after organization operations
  const afterOrganizationCreate = () => {
    showSnackbar('Organization created successfully', 'success');
    fetchOrganizations();
  };

  const errorOrganizationCreation = (error) => {
    showSnackbar(error.response?.data?.message || 'Failed to create organization', 'error');
  };

  const errorOrganizationUpdate = (error) => {
    showSnackbar(error.response?.data?.message || 'Failed to update organization', 'error');
  };

  const afterOrganizationUpdate = () => {
    showSnackbar('Organization updated successfully', 'success');
    fetchOrganizations();
  };

  const breadcrumbItems = [
    { label: "Administration" },
    { label: activePage}
  ];

  // Row action buttons
  const rowActionButtons = [
    {
      name: "Edit",
      tooltip: "Edit Organization",
      icon: <PencilIcon className="icon" />,
      action: (organization) => handleOrganizationEdit(organization),
      disabled: isMultiAction,
    },
    {
      name: "Delete",
      tooltip: "Delete Organization",
      icon: <TrashIcon className="icon" />,
      action: (organization) => handleOrganizationDelete(organization),
      disabled: isMultiAction,
    },
  ];

  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      minWidth: 55,
      maxWidth: 55,
      resizable: false,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => renderTableCell(params.value),
    },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 250,
      maxWidth: 300,
      renderCell: (params) => renderTableCell(params.value),
    },
    {
      field: "Actions",
      headerName: 'Actions',
      minWidth: 90,
      maxWidth: 90,
      headerAlign: 'center',
      sortable: false,
      align: 'center',
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => (
        <RowActionMenu 
          menuItems={rowActionButtons} 
          rowData={params.row}
          isDisabled={isMultiAction}
        />
      ),
    },
  ];

  // Header action buttons
  const headerActionButtons = [
    {
      name: "Add",
      tooltip: "Add New Organization",
      icon: <PlusIcon className="icon" />,
      action: handleAdd,
      disabled: loading,
    },
    {
      name: "Delete",
      tooltip: "Delete Selected Organizations",
      icon: <TrashIcon className="icon" />,
      action: handleDelete,
      disabled: selectedRows.length === 0 || loading,
    },
  ];

  return (
    <>
    <Box className={styles['page-container']}>
      <Box>
        <PageHeader 
          activePage={activePage} 
          breadcrumbItems={breadcrumbItems} 
          title="Organizations"
          description="Manage your organizations"
        />
      </Box>
      <Box className={styles['cover-container-col']}>
        <TableComponent
          title="Organizations List:"
          headerActionButtons={headerActionButtons}
          rows={organizations}
          columns={columns}
          loading={loading}
          idField="id"
          isCheckbox={true}
          isRowSelection={true}
          handleRowSelection={(selectionModel) => setSelectedRows(selectionModel)}
          selectedRows={selectedRows}
          hideColumns={['name']}
        />
       </Box>
    </Box>

      {/* Create */}
      <CreateOrganizationModal
        open={isCreating}
        onClose={() => setIsCreating(false)}
        onSuccess={afterOrganizationCreate}
        onError={errorOrganizationCreation}
      />

      {/* Edit */}
      <EditOrganizationModal
        open={isEditing}
        onClose={() => setIsEditing(false)}
        onSuccess={afterOrganizationUpdate}
        onError={errorOrganizationUpdate}
        organization={organizationForAction}
      />

      {/* Delete */}
      <ConfirmationModal
        open={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={confirmDelete}
        title={getDeleteModalTitle()}
        message={getDeleteConfirmMessage('organization', selectedRows.length, isMultiAction)}
      />
    </>
  );
};

export default Organizations; 